import { useActiveVkuiLocation } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import {
  cabinetApi,
  formatDate,
  formatDateTime,
  type FormDecision,
  type FormHistoryItem,
} from '../../api/cabinet';
import { usePagedLoader } from '../../api/paging';
import { REVIEW_STATUS_LABELS, type ReviewStatus } from '../../api/types';
import { PANEL } from '../../app/router';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { LiveIcon } from '../../shared/icons';
import { docNumber } from '../../shared/lib/docNumber';
import {
  Card,
  CardHeading,
  CardList,
  EmptyState,
  ErrorState,
  Gap,
  LoadingState,
  Mark,
  Meta,
  Muted,
  Notice,
  Prose,
  Screen,
  Section,
  StatusBadge,
  type StatusTone,
} from '../../shared/ui';
import { VersionDetails } from '../property/versionDetails';
import { personName, ShowMore } from '../review/staffKit';

/**
 * История анкеты своим экраном, а не блоком в хабе.
 *
 * В хабе она стояла третьим разделом и тянула страницу вниз на десяток
 * карточек, хотя открывают её редко: игрок приходит в кабинет за состоянием
 * анкеты и за имуществом. Теперь в карточку анкеты ведёт кнопка-значок, а
 * список живёт отдельно и грузит свои страницы сам.
 *
 * Текста версий список не везёт — каждая строка читает свою по требованию,
 * раскрывашкой внутри. Вердикт при этом показываем прямо в строке: решения
 * приезжают вместе со списком, и ради «почему отклонили» игроку незачем
 * уходить в ленту решений.
 */
export function FormHistoryPanel({ id }: NavIdProps) {
  const back = useBack();
  const { panel } = useActiveVkuiLocation();
  const active = panel === PANEL.CHARACTER_FORM_HISTORY;

  const versions = usePagedLoader<FormHistoryItem>(
    (params, signal) => cabinetApi.formHistory(params, signal),
    [],
    active,
  );

  const items = versions.data?.items ?? [];

  return (
    <Screen
      id={id}
      title={t('character.section.history')}
      back={back}
      count={versions.data?.total}
      unit="version"
    >
      <Section>
        {versions.loading && versions.data === null ? <LoadingState /> : null}

        {versions.error !== null && versions.data === null ? (
          <ErrorState message={versions.error} onRetry={versions.reload} />
        ) : null}

        {versions.data !== null && items.length === 0 ? (
          <EmptyState title={t('character.history.empty.title')}>
            {t('character.history.empty.text')}
          </EmptyState>
        ) : null}

        <CardList>
          {items.map((item) => {
            // Решения отсортированы по дате сверху вниз: свежее — первое.
            const decision = item.decisions[0] ?? null;

            return (
              <Card key={item.versionId}>
                {/* Статус стоит вплотную к номеру версии — он про неё, — а
                    отметка «опубликована» уезжает вправо: она про то, где эта
                    версия сейчас, и в макете это не третий чип, а значок. */}
                <CardHeading
                  trailing={
                    item.isPublished ? (
                      <Mark icon={<LiveIcon />}>{t('character.mark.published')}</Mark>
                    ) : undefined
                  }
                  badge={
                    <StatusBadge tone={VERSION_TONE[item.status]}>
                      {REVIEW_STATUS_LABELS[item.status].toLocaleLowerCase('ru')}
                    </StatusBadge>
                  }
                >
                  {t('character.history.version', { no: docNumber(item.versionNo) })}
                </CardHeading>
                <Gap step="s1h" />
                <Meta>
                  {[
                    item.submittedAt === null
                      ? t('character.meta.notSubmitted')
                      : t('character.meta.submittedAt', {
                          date: formatDateTime(item.submittedAt),
                        }),
                    item.publishedAt === null
                      ? null
                      : t('character.meta.publishedSince', { date: formatDate(item.publishedAt) }),
                  ]}
                </Meta>
                <DecisionLine decision={decision} />
                <VersionDetails target="form" versionId={item.versionId} />
              </Card>
            );
          })}
        </CardList>

        <ShowMore page={versions.data} onMore={versions.loadMore} loading={versions.loading} />
      </Section>
    </Screen>
  );
}

/** Кто и когда решил — и, если сказал почему, его слова целиком. */
function DecisionLine({ decision }: { decision: FormDecision | null }) {
  if (decision === null) {
    return (
      <>
        <Gap step="s2" />
        <Muted>{t('character.empty.noDecision')}</Muted>
      </>
    );
  }

  return (
    <>
      {decision.comment === null ? null : (
        <>
          <Gap step="s2h" />
          <Notice tone={decision.verdict === 'reject' ? 'error' : 'success'}>
            <Prose>{decision.comment}</Prose>
          </Notice>
        </>
      )}
      <Gap step="s2" />
      <Muted>
        {personName(decision.moderator)} · {formatDate(decision.decidedAt)}
      </Muted>
    </>
  );
}

/** Контур карточки повторяет значок: состояние видно раньше, чем прочитан текст. */
/** Черновик — единственный статус без своего цвета: он ещё никуда не ушёл. */
const VERSION_TONE: Record<ReviewStatus, StatusTone> = {
  draft: 'archived',
  pending: 'pending',
  approved: 'published',
  rejected: 'rejected',
};
